import React from 'react';
import { getAllCardTypes } from '../../utils/cardDetection';
import './CreditCardLogos.css';

/**
 * Credit Card Logos Component
 * Displays all supported credit card logos with dynamic highlighting
 */
const CreditCardLogos = ({ 
  detectedCardType = null, 
  size = 'medium',
  className = '',
  showLabels = false 
}) => {
  const allCardTypes = getAllCardTypes();

  const getSizeClass = () => {
    switch (size) {
      case 'small': return 'card-logos-small';
      case 'large': return 'card-logos-large';
      default: return 'card-logos-medium';
    }
  };

  return (
    <div className={`credit-card-logos ${getSizeClass()} ${className}`}>
      {showLabels && (
        <div className="card-logos-label">
          Accepted Cards:
        </div>
      )}
      <div className="card-logos-container">
        {allCardTypes.map((cardType) => {
          const isActive = detectedCardType?.type === cardType.type;
          const isInactive = detectedCardType && !isActive;
          
          return (
            <div
              key={cardType.type}
              className={`card-logo ${isActive ? 'active' : ''} ${isInactive ? 'inactive' : ''}`}
              title={cardType.name}
            >
              <CardLogo type={cardType.type} name={cardType.name} />
            </div>
          );
        })}
      </div>
    </div>
  );
};

/**
 * Individual Card Logo Component
 * Renders SVG or styled logo for each card type
 */
const CardLogo = ({ type, name }) => {
  const logoComponents = {
    visa: (
      <svg viewBox="0 0 40 24" className="card-logo-svg">
        <rect width="40" height="24" rx="4" fill="#1a1f71"/>
        <text x="20" y="16" textAnchor="middle" fill="white" fontSize="8" fontWeight="bold">
          VISA
        </text>
      </svg>
    ),
    mastercard: (
      <svg viewBox="0 0 40 24" className="card-logo-svg">
        <rect width="40" height="24" rx="4" fill="#eb001b"/>
        <circle cx="15" cy="12" r="7" fill="#ff5f00"/>
        <circle cx="25" cy="12" r="7" fill="#f79e1b"/>
        <text x="20" y="20" textAnchor="middle" fill="white" fontSize="4" fontWeight="bold">
          mastercard
        </text>
      </svg>
    ),
    amex: (
      <svg viewBox="0 0 40 24" className="card-logo-svg">
        <rect width="40" height="24" rx="4" fill="#006fcf"/>
        <text x="20" y="14" textAnchor="middle" fill="white" fontSize="6" fontWeight="bold">
          AMEX
        </text>
      </svg>
    ),
    discover: (
      <svg viewBox="0 0 40 24" className="card-logo-svg">
        <rect width="40" height="24" rx="4" fill="#ff6000"/>
        <text x="20" y="16" textAnchor="middle" fill="white" fontSize="7" fontWeight="bold">
          DISCOVER
        </text>
      </svg>
    ),
  };

  return logoComponents[type] || (
    <svg viewBox="0 0 40 24" className="card-logo-svg">
      <rect width="40" height="24" rx="4" fill="#666" stroke="#999" strokeWidth="1"/>
      <text x="20" y="16" textAnchor="middle" fill="white" fontSize="6">
        {name}
      </text>
    </svg>
  );
};

/**
 * Single Card Logo Display (for payment details)
 */
export const SingleCardLogo = ({ cardType, size = 'small', className = '' }) => {
  if (!cardType) return null;

  const getSizeClass = () => {
    switch (size) {
      case 'small': return 'single-card-logo-small';
      case 'large': return 'single-card-logo-large';
      default: return 'single-card-logo-medium';
    }
  };

  return (
    <div className={`single-card-logo ${getSizeClass()} ${className}`}>
      <CardLogo type={cardType.type} name={cardType.name} />
    </div>
  );
};

export default CreditCardLogos;
